import type { GridPlan } from '../resolution.ts';
import type { SnowpackState } from '../scene.ts';
import {
  addLayer,
  columnDepth,
  columnMass,
  columnWater,
  Grain,
  type SnowColumns,
} from '../snowpack.ts';
import { updateStabilityCell } from '../stability.ts';
import type { FlowResult } from './flow.ts';

/**
 * Write an avalanche back into the snowpack: remove the released and entrained snow from the top
 * of each column and put the debris on as a new layer. Flow fields live on the terrain grid;
 * they are block-averaged onto the (coarser or equal) snowpack cells.
 *
 * Every change is recorded in a patch (the affected columns as they were), so a run can be
 * undone exactly.
 */
export interface ColumnPatch {
  cells: Int32Array;
  count: Uint8Array;
  /** Per cell, `maxLayers` entries of each layer array. */
  mass: Float32Array;
  density: Float32Array;
  grain: Uint8Array;
  depositedAt: Float32Array;
  water: Float32Array;
}

export interface ApplyResult {
  patch: ColumnPatch;
  /** Snowpack cells whose columns changed. */
  touched: Int32Array;
}

/** Block-sum a terrain-grid field (kg/m²) onto snowpack cells, as the mean over each block. */
function blockMeans(
  plan: GridPlan,
  field: Float32Array,
  cells: Set<number>,
  out: Map<number, number>,
) {
  const k = plan.snowpack.cellSize / plan.terrain.cellSize;
  const t = plan.terrain;
  for (let i = 0; i < field.length; i++) {
    const v = field[i]!;
    if (v === 0) continue;
    const sc =
      Math.floor(Math.floor(i / t.cols) / k) * plan.snowpack.cols + Math.floor((i % t.cols) / k);
    out.set(sc, (out.get(sc) ?? 0) + v / (k * k));
    cells.add(sc);
  }
}

/** Remove `mass` kg/m² from the top of a column (any layers). Returns what was removed. */
function removeTop(cols: SnowColumns, cell: number, mass: number): number {
  const base = cell * cols.maxLayers;
  let remaining = mass;
  while (remaining > 1e-6 && cols.count[cell]! > 0) {
    const top = base + cols.count[cell]! - 1;
    const take = Math.min(remaining, cols.mass[top]!);
    const f = cols.mass[top]! > 0 ? take / cols.mass[top]! : 1;
    cols.water[top] = cols.water[top]! * (1 - f);
    cols.mass[top] = cols.mass[top]! - take;
    remaining -= take;
    if (cols.mass[top]! <= 1e-4) {
      cols.mass[top] = 0;
      cols.water[top] = 0;
      cols.count[cell]!--;
    }
  }
  return mass - remaining;
}

export function applyAvalanche(
  plan: GridPlan,
  snow: SnowpackState,
  flow: FlowResult,
  debrisDensity: number,
): ApplyResult {
  const cols = snow.columns;
  const removed = new Map<number, number>();
  const deposited = new Map<number, number>();
  const cells = new Set<number>();
  blockMeans(plan, flow.released, cells, removed);
  blockMeans(plan, flow.entrained, cells, removed);
  blockMeans(plan, flow.deposit, cells, deposited);

  const list = Int32Array.from([...cells].sort((a, b) => a - b));
  const patch = snapshot(cols, list);

  for (const c of list) {
    const rem = removed.get(c) ?? 0;
    if (rem > 0) removeTop(cols, c, rem);
    const dep = deposited.get(c) ?? 0;
    if (dep > 0) addLayer(cols, c, dep, debrisDensity, Grain.Debris, snow.evaluationHour);
  }
  refreshCells(snow, list);
  return { patch, touched: list };
}

/** Restore the columns saved in a patch and refresh derived fields. */
export function undoAvalanche(snow: SnowpackState, patch: ColumnPatch): void {
  const cols = snow.columns;
  const L = cols.maxLayers;
  patch.cells.forEach((c, j) => {
    cols.count[c] = patch.count[j]!;
    cols.mass.set(patch.mass.subarray(j * L, (j + 1) * L), c * L);
    cols.density.set(patch.density.subarray(j * L, (j + 1) * L), c * L);
    cols.grain.set(patch.grain.subarray(j * L, (j + 1) * L), c * L);
    cols.depositedAt.set(patch.depositedAt.subarray(j * L, (j + 1) * L), c * L);
    cols.water.set(patch.water.subarray(j * L, (j + 1) * L), c * L);
  });
  refreshCells(snow, patch.cells);
}

function snapshot(cols: SnowColumns, cells: Int32Array): ColumnPatch {
  const L = cols.maxLayers;
  const n = cells.length;
  const p: ColumnPatch = {
    cells,
    count: new Uint8Array(n),
    mass: new Float32Array(n * L),
    density: new Float32Array(n * L),
    grain: new Uint8Array(n * L),
    depositedAt: new Float32Array(n * L),
    water: new Float32Array(n * L),
  };
  cells.forEach((c, j) => {
    p.count[j] = cols.count[c]!;
    p.mass.set(cols.mass.subarray(c * L, (c + 1) * L), j * L);
    p.density.set(cols.density.subarray(c * L, (c + 1) * L), j * L);
    p.grain.set(cols.grain.subarray(c * L, (c + 1) * L), j * L);
    p.depositedAt.set(cols.depositedAt.subarray(c * L, (c + 1) * L), j * L);
    p.water.set(cols.water.subarray(c * L, (c + 1) * L), j * L);
  });
  return p;
}

/** Recompute depth, SWE and stability for the given snowpack cells. */
function refreshCells(snow: SnowpackState, cells: Int32Array): void {
  const cols = snow.columns;
  for (const c of cells) {
    snow.depth.data[c] = columnDepth(cols, c);
    snow.swe.data[c] = columnMass(cols, c) + columnWater(cols, c);
    updateStabilityCell(
      snow.stability,
      cols,
      c,
      snow.topo.slope,
      snow.airTemp,
      snow.evaluationHour,
    );
  }
}
